import { useState, useEffect, useCallback } from 'react';
import { getSurveyList, getForMemberResponse, submitMemberAnswer } from '../api/surveyApi';

const YEAR = new Date().getFullYear().toString();

const getYoutubeEmbedUrl = (url) => {
  if (!url) return null;
  const m = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/shorts\/)([a-zA-Z0-9_-]{11})/);
  return m ? `https://www.youtube.com/embed/${m[1]}` : null;
};

export default function SurveyMemberPage({ onLogout }) {
  const [view, setView]   = useState('list');  // list | response
  const [list, setList]   = useState([]);
  const [selected, setSelected] = useState(null);
  const [data, setData]   = useState(null);
  const [answers, setAnswers] = useState({});
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState('');
  const [done, setDone]   = useState(false);

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 2500); };

  const loadList = useCallback(async () => {
    const res = await getSurveyList(YEAR);
    if (res.success) {
      setList(res.data.filter(s => s.target_type === 'U' && s.status === 'P'));
    } else if (res.message === '토큰이 만료되었습니다. 다시 로그인해주세요.') {
      onLogout();
    }
  }, [onLogout]);

  useEffect(() => { loadList(); }, [loadList]);

  const handleOpen = async (item) => {
    setSelected(item);
    setError('');
    setDone(false);
    setLoading(true);
    try {
      const res = await getForMemberResponse(item.survey_year, item.seq_no);
      if (res.success) {
        setData(res.data);
        // 기존 답변 프리필 (체크박스는 쉼표 분리해서 배열로)
        const prefill = {};
        (res.data.myAnswers || []).forEach(a => {
          const item = res.data.items.find(it => it.item_no === a.item_no);
          prefill[a.item_no] = item?.item_type === 'C' ? a.answer_text.split(',').filter(Boolean) : a.answer_text;
        });
        setAnswers(prefill);
        setView('response');
      } else {
        showToast(`❌ ${res.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const setAnswer = (item_no, val) => setAnswers(p => ({ ...p, [item_no]: val }));

  const toggleCheckbox = (item_no, optName) => {
    setAnswers(p => {
      const cur = Array.isArray(p[item_no]) ? p[item_no] : [];
      const next = cur.includes(optName) ? cur.filter(v => v !== optName) : [...cur, optName];
      return { ...p, [item_no]: next };
    });
  };

  const handleSubmit = async () => {
    setError('');
    const questionItems = data.items.filter(it => it.item_type !== 'S' && it.item_type !== 'V');

    for (const it of questionItems) {
      if (it.required_yn === 'Y') {
        const v = answers[it.item_no];
        const empty = !v || (Array.isArray(v) && v.length === 0) || (typeof v === 'string' && v.trim() === '');
        if (empty) { setError(`"${it.item_title}" 항목은 필수 응답입니다.`); return; }
      }
    }

    setLoading(true);
    try {
      const payload = questionItems
        .filter(it => answers[it.item_no] !== undefined)
        .map(it => ({
          item_no: it.item_no,
          answer_text: Array.isArray(answers[it.item_no]) ? answers[it.item_no].join(',') : String(answers[it.item_no])
        }));

      const res = await submitMemberAnswer(selected.survey_year, selected.seq_no, payload);
      if (res.success) setDone(true);
      else setError(res.message);
    } finally {
      setLoading(false);
    }
  };

  // ==================== 응답 화면 ====================
  if (view === 'response') {
    if (loading && !data) return <div style={S.wrap}><div style={{ textAlign: 'center', padding: 60, color: '#aaa' }}>로딩 중...</div></div>;

    if (done) return (
      <div style={S.wrap}>
        <button style={S.backBtn} onClick={() => { setView('list'); loadList(); }}>← 목록으로</button>
        <div style={S.card}>
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <div style={{ fontSize: 60, marginBottom: 16 }}>✅</div>
            <h2 style={{ fontSize: 20, fontWeight: 800, marginBottom: 8 }}>제출이 완료되었습니다!</h2>
            <p style={{ fontSize: 14, color: '#888' }}>소중한 의견 감사합니다 😊</p>
          </div>
        </div>
      </div>
    );

    if (!data) return null;

    return (
      <div style={S.wrap}>
        <button style={S.backBtn} onClick={() => setView('list')}>← 목록으로</button>
        <div style={S.header}>
          <h1 style={S.title}>{data.title}</h1>
          {data.myAnswers?.length > 0 && (
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.8)', marginTop: 4 }}>
              💡 이전에 제출한 답변을 수정할 수 있습니다.
            </div>
          )}
        </div>

        {data.items.map(item => {
          if (item.item_type === 'S') {
            return (
              <div key={item.item_no} style={S.sectionTitle}>
                {item.img_data && (
                  <img src={item.img_data} alt="섹션이미지"
                    style={{ width: '100%', borderRadius: 8, marginBottom: 12, maxHeight: 240, objectFit: 'contain', background: '#f5f5f5' }} />
                )}
                <div style={{ fontWeight: 900, fontSize: 19, color: '#1a1a2e' }}>{item.item_title}</div>
              </div>
            );
          }

          if (item.item_type === 'V') {
            const embedUrl = getYoutubeEmbedUrl(item.item_title);
            return (
              <div key={item.item_no} style={S.card}>
                {embedUrl ? (
                  <div style={{ aspectRatio: '16/9', width: '100%' }}>
                    <iframe
                      src={embedUrl}
                      style={{ width: '100%', height: '100%', border: 'none', borderRadius: 8 }}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      title={`survey-video-${item.item_no}`} />
                  </div>
                ) : (
                  <div style={{ color: '#aaa', fontSize: 13 }}>⚠️ 영상을 불러올 수 없습니다.</div>
                )}
              </div>
            );
          }

          return (
            <div key={item.item_no} style={S.card}>
              {item.img_data && (
                <img src={item.img_data} alt="질문이미지"
                  style={{ width: '100%', borderRadius: 8, marginBottom: 12, maxHeight: 300, objectFit: 'contain', background: '#f5f5f5' }} />
              )}

              <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 16 }}>
                {item.item_title} {item.required_yn === 'Y' && <span style={{ color: '#ff4d4f' }}>*</span>}
              </div>

              {item.item_type === 'T1' && (
                <input style={S.input}
                  value={answers[item.item_no] || ''}
                  onChange={e => setAnswer(item.item_no, e.target.value)}
                  placeholder="답변을 입력해주세요" />
              )}

              {item.item_type === 'T2' && (
                <textarea style={{ ...S.input, minHeight: 100, resize: 'vertical' }}
                  value={answers[item.item_no] || ''}
                  onChange={e => setAnswer(item.item_no, e.target.value)}
                  placeholder="자유롭게 작성해주세요" />
              )}

              {item.item_type === 'M' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {item.options.map(o => (
                    <button key={o.opt_no}
                      style={{ ...S.optBtn, background: answers[item.item_no] === o.opt_name ? '#1677ff' : '#f5f5f5', color: answers[item.item_no] === o.opt_name ? '#fff' : '#333' }}
                      onClick={() => setAnswer(item.item_no, o.opt_name)}>
                      {o.opt_name}
                    </button>
                  ))}
                </div>
              )}

              {item.item_type === 'C' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {item.options.map(o => {
                    const checked = Array.isArray(answers[item.item_no]) && answers[item.item_no].includes(o.opt_name);
                    return (
                      <button key={o.opt_no}
                        style={{ ...S.optBtn, background: checked ? '#52c41a' : '#f5f5f5', color: checked ? '#fff' : '#333' }}
                        onClick={() => toggleCheckbox(item.item_no, o.opt_name)}>
                        {checked ? '☑ ' : '☐ '}{o.opt_name}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}

        {error && <div style={S.error}>⚠️ {error}</div>}

        <button style={{ ...S.submitBtn, opacity: loading ? 0.7 : 1 }} onClick={handleSubmit} disabled={loading}>
          {loading ? '제출 중...' : (data.myAnswers?.length > 0 ? '다시 제출하기' : '제출하기')}
        </button>
      </div>
    );
  }

  // ==================== 목록 화면 ====================
  return (
    <div style={S.wrap}>
      {toast && <div style={S.toast}>{toast}</div>}
      <div style={S.header}>
        <h1 style={S.title}>📋 설문조사</h1>
      </div>

      {list.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 60, color: '#aaa' }}>현재 진행중인 설문이 없습니다.</div>
      ) : list.map(item => (
        <div key={item.seq_no} style={S.listCard} onClick={() => handleOpen(item)}>
          <div style={{ fontWeight: 700, fontSize: 16 }}>{item.title}</div>
          <div style={{ fontSize: 12, color: '#aaa', marginTop: 4 }}>{item.reg_dt?.slice(0, 10)} 등록</div>
          <div style={{ marginTop: 8, color: '#1677ff', fontSize: 13, fontWeight: 600 }}>응답하기 →</div>
        </div>
      ))}
    </div>
  );
}

const S = {
  wrap        : { maxWidth: 520, margin: '0 auto', padding: 16 },
  header      : { background: 'linear-gradient(135deg, #1677ff, #4096ff)', borderRadius: 16, padding: '24px 20px', marginBottom: 16, textAlign: 'center' },
  title       : { fontSize: 20, fontWeight: 800, color: '#fff', margin: 0 },
  backBtn     : { background: 'none', border: 'none', fontSize: 14, color: '#1677ff', cursor: 'pointer', padding: 0, marginBottom: 12 },
  card        : { background: '#fff', borderRadius: 12, padding: 16, boxShadow: '0 2px 8px rgba(0,0,0,0.06)', marginBottom: 12 },
  listCard    : { background: '#fff', borderRadius: 12, padding: 16, boxShadow: '0 2px 8px rgba(0,0,0,0.06)', marginBottom: 12, cursor: 'pointer' },
  sectionTitle: { padding: '12px 4px 4px', borderTop: '4px solid #1677ff', marginTop: 8, marginBottom: 4 },
  optBtn      : { border: 'none', borderRadius: 10, padding: '12px 16px', fontSize: 14, fontWeight: 600, cursor: 'pointer', textAlign: 'left', transition: 'all 0.15s' },
  input       : { border: '1.5px solid #e0e0e0', borderRadius: 8, padding: '10px 12px', fontSize: 14, outline: 'none', width: '100%', boxSizing: 'border-box' },
  error       : { background: '#fff5f5', border: '1px solid #ffcccc', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: '#c0392b', marginBottom: 12 },
  submitBtn   : { width: '100%', background: 'linear-gradient(135deg, #1677ff, #4096ff)', color: '#fff', border: 'none', borderRadius: 12, padding: 16, fontSize: 16, fontWeight: 700, cursor: 'pointer' },
  toast       : { position: 'fixed', top: 20, left: '50%', transform: 'translateX(-50%)', background: '#1a1a2e', color: '#fff', padding: '10px 20px', borderRadius: 8, fontSize: 13, zIndex: 999 },
};